import { type MaybeRef, onMounted, onUnmounted, unref } from 'vue'
import type { PhotoSwipeInstance, PhotoSwipeOptions } from '@/modules/chat-matrix-fast/types/IChat'
import { CHAT_FLOATING_Z_INDEX } from '@/modules/chat-matrix-fast/constants/ui'
import 'photoswipe/style.css'

const activeInstances = new Set<PhotoSwipeInstance>()

const PSWP_STYLE_ID = 'chat-matrix-pswp-styles-v2'

// z-index above SlidingChat; video slide sizing (chat-matrix scss not imported globally).
function ensurePhotoSwipeStyles() {
  if (typeof document === 'undefined' || document.getElementById(PSWP_STYLE_ID)) {
    return
  }

  const style = document.createElement('style')
  style.id = PSWP_STYLE_ID
  style.textContent = `
.pswp { --pswp-root-z-index: ${CHAT_FLOATING_Z_INDEX + 1}; }
.pswp__zoom-wrap:has(.pswp-video-container),
.pswp__content:has(.pswp-video-container) {
  width: 100% !important;
  height: 100% !important;
}
.pswp-video-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: transparent;
}
.pswp-video-container video {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  background: #000;
}
`.trim()
  document.head.appendChild(style)
}

export const hasActivePhotoSwipe = () => activeInstances.size > 0

type UsePhotoSwipeOptions = Omit<PhotoSwipeOptions, 'gallery'> & {
  gallery?: MaybeRef<string | HTMLElement | null | undefined>
}

function resolveGallery(
  gallery: MaybeRef<string | HTMLElement | null | undefined> | undefined,
): string | HTMLElement | null {
  const value = unref(gallery)
  if (!value) {
    return null
  }

  return value
}

function pauseGalleryVideos() {
  document.querySelectorAll('.pswp video').forEach(video => {
    video.pause()
  })
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

function buildVideoItemHtml(videoSrc: string): string {
  return [
    '<div class="pswp-video-container" style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;background:transparent">',
    `<video src="${escapeAttr(videoSrc)}" controls playsinline preload="metadata" style="max-width:100%;max-height:100%;width:auto;height:auto;background:#000"></video>`,
    '</div>',
  ].join('')
}

/** Video slide size = viewport — else zoom-wrap collapses to intrinsic video at (0,0). */
function getVideoSlideSize(): { width: number; height: number } {
  return {
    width: Math.max(1, Math.round(window.innerWidth)),
    height: Math.max(1, Math.round(window.innerHeight)),
  }
}

function applyVideoItemData(itemData: Record<string, unknown>, videoSrc: string): void {
  const { width, height } = getVideoSlideSize()
  itemData.html = buildVideoItemHtml(videoSrc)
  itemData.type = 'html'
  itemData.src = undefined
  itemData.width = width
  itemData.height = height
}

/** PhotoSwipe тянет layout из width/height item — при 800×600 чужие пропорции ломаются. */
function syncImageSlideToNaturalSize(slide: {
  width?: number
  height?: number
  data?: Record<string, unknown>
  content?: { element?: unknown; width?: number; height?: number }
  calculateSize?: () => void
  zoomAndPanToInitial?: () => void
  applyCurrentZoomPan?: () => void
  updateContentSize?: (force?: boolean) => void
} | null | undefined): void {
  const el = slide?.content?.element
  if (!(el instanceof HTMLImageElement)) {
    return
  }

  const width = el.naturalWidth
  const height = el.naturalHeight
  if (!width || !height) {
    return
  }

  if (slide.width === width && slide.height === height) {
    return
  }

  slide.width = width
  slide.height = height
  if (slide.content) {
    slide.content.width = width
    slide.content.height = height
  }
  if (slide.data) {
    slide.data.width = width
    slide.data.height = height
    slide.data.w = width
    slide.data.h = height
  }

  slide.calculateSize?.()
  slide.zoomAndPanToInitial?.()
  slide.applyCurrentZoomPan?.()
  slide.updateContentSize?.(true)
}

function setupImageDimensionSync(lightbox: PhotoSwipeInstance): void {
  lightbox.on('loadComplete', ({ slide }) => {
    syncImageSlideToNaturalSize(slide as Parameters<typeof syncImageSlideToNaturalSize>[0])
  })

  lightbox.on('change', () => {
    const pswp = (lightbox as PhotoSwipeInstance & {
      pswp?: { currSlide?: Parameters<typeof syncImageSlideToNaturalSize>[0] }
    }).pswp
    syncImageSlideToNaturalSize(pswp?.currSlide)
  })
}

function setupVideoSupport(lightbox: PhotoSwipeInstance) {
  lightbox.on('itemData', ({ itemData }) => {
    const element = itemData?.element as HTMLAnchorElement | undefined
    if (!element || element.dataset.pswpType !== 'video') {
      return
    }

    const videoSrc = element.dataset.pswpVideoSrc
      || element.dataset.pswpSrc
      || element.href

    if (!videoSrc || !itemData) {
      return
    }

    applyVideoItemData(itemData, videoSrc)
  })

  lightbox.on('change', pauseGalleryVideos)
  lightbox.on('close', pauseGalleryVideos)
}

export async function openVideoPreview(videoUrl: string): Promise<void> {
  if (!videoUrl || typeof window === 'undefined') {
    return
  }

  ensurePhotoSwipeStyles()

  const lightboxModule = await import('photoswipe/lightbox')
  const PhotoSwipeLightbox = lightboxModule.default
  const { width, height } = getVideoSlideSize()

  const lightbox = new PhotoSwipeLightbox({
    dataSource: [{
      html: buildVideoItemHtml(videoUrl),
      type: 'html',
      width,
      height,
    }],
    showHideAnimationType: 'fade',
    pswpModule: () => import('photoswipe'),
  }) as PhotoSwipeInstance

  const cleanup = () => {
    pauseGalleryVideos()
    if (typeof lightbox.destroy === 'function') {
      lightbox.destroy()
    }
    activeInstances.delete(lightbox)
  }

  lightbox.on('change', pauseGalleryVideos)
  lightbox.on('close', cleanup)
  lightbox.init()
  activeInstances.add(lightbox)
  lightbox.loadAndOpen?.(0)
}

/** Открыть картинку со свежим src (после idle blob/JWT могли протухнуть). */
export async function openImageLightbox(
  imageUrl: string,
  width = 800,
  height = 600,
): Promise<void> {
  await openMediaLightbox({
    items: [{
      src: imageUrl,
      kind: 'image',
      width,
      height,
    }],
    index: 0,
  })
}

export interface MediaLightboxItem {
  src: string
  kind: 'image' | 'video'
  width?: number
  height?: number
}

/** Галерея медиа чата: стрелки и ←/→ в PhotoSwipe. */
export async function openMediaLightbox(options: {
  items: MediaLightboxItem[]
  index?: number
}): Promise<void> {
  if (typeof window === 'undefined' || !options.items.length) {
    return
  }

  ensurePhotoSwipeStyles()

  const lightboxModule = await import('photoswipe/lightbox')
  const PhotoSwipeLightbox = lightboxModule.default
  const startIndex = Math.min(
    Math.max(0, options.index ?? 0),
    options.items.length - 1,
  )

  const dataSource = options.items.map(item => {
    if (item.kind === 'video') {
      const { width, height } = getVideoSlideSize()
      return {
        html: buildVideoItemHtml(item.src),
        type: 'html' as const,
        width,
        height,
      }
    }

    return {
      src: item.src,
      width: Math.max(1, Math.round(item.width || 800)),
      height: Math.max(1, Math.round(item.height || 600)),
    }
  })

  const lightbox = new PhotoSwipeLightbox({
    dataSource,
    index: startIndex,
    showHideAnimationType: 'fade',
    pswpModule: () => import('photoswipe'),
  }) as PhotoSwipeInstance

  const cleanup = () => {
    pauseGalleryVideos()
    if (typeof lightbox.destroy === 'function') {
      lightbox.destroy()
    }
    activeInstances.delete(lightbox)
  }

  setupImageDimensionSync(lightbox)
  lightbox.on('change', pauseGalleryVideos)
  lightbox.on('close', cleanup)
  lightbox.init()
  activeInstances.add(lightbox)
  lightbox.loadAndOpen?.(startIndex)
}

export function usePhotoSwipe(options: UsePhotoSwipeOptions) {
  let lightboxInstance: PhotoSwipeInstance | null = null

  const initPhotoSwipe = async () => {
    if (typeof window === 'undefined' || lightboxInstance) {
      return
    }

    const gallery = resolveGallery(options.gallery)
    if (!gallery) {
      return
    }

    ensurePhotoSwipeStyles()

    const lightboxModule = await import('photoswipe/lightbox')
    const PhotoSwipeLightbox = lightboxModule.default

    lightboxInstance = new PhotoSwipeLightbox({
      gallery,
      children: options.children,
      pswpModule: () => import('photoswipe'),
    })

    setupVideoSupport(lightboxInstance)
    setupImageDimensionSync(lightboxInstance)
    lightboxInstance.init()
    activeInstances.add(lightboxInstance)
  }

  const destroyPhotoSwipe = () => {
    if (!lightboxInstance) {
      return
    }

    pauseGalleryVideos()

    if (typeof lightboxInstance.destroy === 'function') {
      lightboxInstance.destroy()
    }

    activeInstances.delete(lightboxInstance)
    lightboxInstance = null
  }

  onMounted(() => {
    if (!options.lazy) {
      void initPhotoSwipe()
    }
  })

  onUnmounted(() => {
    destroyPhotoSwipe()
  })

  return {
    initPhotoSwipe,
    destroyPhotoSwipe,
  }
}

export function handlePswpGalleryClick(
  event: MouseEvent,
  src?: string | null,
  fallback?: () => void,
) {
  if (!src) {
    return
  }

  event.preventDefault()

  if (!hasActivePhotoSwipe()) {
    fallback?.()
  }
}

export function setImageDimensions(event: Event) {
  const img = event.target as HTMLImageElement
  const anchor = img?.closest('a') as HTMLElement | null

  if (!img || !anchor) {
    return
  }

  const width = img.naturalWidth || img.width
  const height = img.naturalHeight || img.height

  if (width && height) {
    anchor.setAttribute('data-pswp-width', String(width))
    anchor.setAttribute('data-pswp-height', String(height))
  }
}

export function setVideoDimensions(event: Event) {
  const video = event.target as HTMLVideoElement
  const anchor = video?.closest('a') as HTMLElement | null

  if (!video || !anchor) {
    return
  }

  const width = video.videoWidth || video.clientWidth
  const height = video.videoHeight || video.clientHeight

  if (width && height) {
    anchor.setAttribute('data-pswp-width', String(width))
    anchor.setAttribute('data-pswp-height', String(height))
  }
}
